import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled/macro';
import { parseISO } from 'date-fns';
import { Box, Tooltip, Input, InputGroup, InputRightElement } from '@chakra-ui/react';

import { isMainParameter } from '../../../services/batteries';
import { createDateAsUTC, lenientParse } from '../../../services/items';

import Popover from '../../utils/Popover';
import CustomIconButton from '../../utils/CustomIconButton';

import { ReactComponent as WarningIcon } from '../../../icons/warning.svg';

const TimelineTrackElement = ({
    time,
    element,
    parameterDbField,
    onMainPeriodClick,
    onSubPeriodClick,
    onTimelineValueChange,
    ribbonStaticData,
    subParameterData,
    mainWrapperRef,
}) => {
    const intl = useIntl();

    const initialInputValue =
        element.value === 'null' ? intl.formatMessage({ id: 'batteries_timeline_null_value' }) : element.value;

    const [inputValue, setInputValue] = useState(initialInputValue);
    const [isInvalid, setIsInvalid] = useState(null);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [errorMessageId, setErrorMessageId] = useState(null);

    const isMain = isMainParameter(parameterDbField);
    const parameter = ribbonStaticData.parameters.find((p) => p.dbFieldName === parameterDbField);
    const elementStyle = time.toStyleLeftAndWidth(parseISO(element.startDate), parseISO(element.endDate), true);

    const onInputChange = (e) => {
        setIsTooltipOpen(false);
        const value = e.target.value;
        const trimmed = value.trim();
        setInputValue(trimmed);
        const isValid = validateInputValue(trimmed);
        setIsInvalid(!isValid);
    };

    const validateInputValue = (value) => {
        if (!value) {
            setErrorMessageId(null);
            return true;
        }

        const numbersRegExp = new RegExp('^[0-9]+([.][0-9]+)?$');
        if (!numbersRegExp.test(value)) {
            setErrorMessageId('batteries_timeline_number_invalid_value');
            return false;
        }

        setErrorMessageId(null);
        return true;
    };

    const onKeyPress = (e) => {
        const translatedNull = intl.formatMessage({ id: 'batteries_timeline_null_value' });

        if (e.key === 'Enter' && !isInvalid && e.target.value !== translatedNull) {
            const payload = {
                parameter: parameter.value,
                parameterPeriod: {
                    startDate: createDateAsUTC(lenientParse(element.startDate)),
                    endDate: createDateAsUTC(lenientParse(element.endDate)),
                    value: e.target.value === '' ? null : Number(e.target.value),
                },
                subParameter: {},
            };

            // payload object's subParameter gets populated with data depending on the period type
            if (!isMain) {
                payload.subParameter = {
                    peakPeriodId: Number(subParameterData.peakPeriodId) || null,
                    productTypeId: Number(subParameterData.productTypeId) || null,
                    batteryLevel: subParameterData.batteryLevel,
                    percentOutage: subParameterData.percentOutage,
                };
            }

            onTimelineValueChange(payload, element.startDate);
        }
    };

    const trackInput = (
        <TrackElementWrapper
            style={elementStyle}
            onClick={() => {
                if (!isInvalid) {
                    isMain ? onMainPeriodClick(element, parameterDbField) : onSubPeriodClick(element, parameterDbField);
                }
            }}
            onMouseOver={() => setIsTooltipOpen(true)}
            onMouseOut={() => setIsTooltipOpen(false)}
        >
            <InputGroup width="100px">
                <StyledInput
                    type="text"
                    value={inputValue}
                    onChange={onInputChange}
                    isInvalid={isInvalid}
                    onKeyPress={onKeyPress}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={() => setIsTooltipOpen(false)}
                    ml={1}
                />
                {isInvalid === true && (
                    <StyledRightElement
                        zIndex={1}
                        children={
                            <Popover
                                strategy="fixed"
                                placement="bottom"
                                trigger={
                                    <StyledCustomIconButton
                                        icon={<WarningIcon />}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                }
                            >
                                <FormattedMessage id={errorMessageId} />
                            </Popover>
                        }
                    />
                )}
            </InputGroup>
        </TrackElementWrapper>
    );

    const tooltipLabel = isInvalid ? '' : inputValue === 0 ? inputValue.toString() : inputValue;

    // add popper modifier to chakra's tooltip to prevent it from overflowing the main content wrapper
    const overflowBoundary = mainWrapperRef
        ? mainWrapperRef.current
        : document.querySelector(`[data-id="main-ribbon-wrapper"]`);

    const popperModifiers = [
        {
            name: 'preventOverflow',
            options: {
                boundary: overflowBoundary,
            },
        },
    ];

    const track = (
        <TrackTooltip
            label={tooltipLabel}
            isOpen={isTooltipOpen}
            isDisabled={!isTooltipOpen || isInvalid}
            modifiers={popperModifiers}
        >
            {trackInput}
        </TrackTooltip>
    );

    // for yearly view, if given element spans for more than six consecutive months (hence having > 6 periodIds) we have space to show the default element;
    // otherwise we have to show a tooltip and hide the label since there is no place for it
    const hasMultiplePeriodIds = element.periodIds && element.periodIds.length > 6;

    return time.selectedView === 1 || (time.selectedView === 2 && hasMultiplePeriodIds) ? (
        track
    ) : (
        <TrackTooltip label={tooltipLabel} modifiers={popperModifiers}>
            <TrackElementWrapper
                style={elementStyle}
                onClick={() => {
                    if (!isInvalid) {
                        isMain
                            ? onMainPeriodClick(element, parameterDbField)
                            : onSubPeriodClick(element, parameterDbField);
                    }
                }}
            />
        </TrackTooltip>
    );
};

const TrackElementWrapper = styled(Box)`
    position: absolute;
    top: 2px;
    height: 46px;
    background: var(--chakra-colors-blue-100);
    border-radius: 8px;
    text-align: center;
`;

const TrackTooltip = styled(Tooltip)`
    border-radius: 6px;
    background: var(--chakra-colors-gray-500);
    color: var(--chakra-colors-white);
    font-size: 10px;
`;

const StyledInput = styled(Input)`
    background: var(--chakra-colors-white);
    font-size: 13px;
    width: 100px;
    top: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover,
    &:focus {
        background: var(--chakra-colors-white);
    }
`;

const StyledRightElement = styled(InputRightElement)`
    [data-popper-reference-hidden] {
        visibility: hidden !important;
        opacity: 0;
        z-index: -1 !important;
        pointer-events: none;
    }
`;

const StyledCustomIconButton = styled(CustomIconButton)`
    svg {
        width: 16px;
        height: 16px;
        margin-top: 2px;
    }
    svg path {
        stroke: var(--chakra-colors-transparent);
    }
`;

export default TimelineTrackElement;
